import {useCallback, useEffect, useMemo, useRef} from 'react'
import {Time, Util} from 'yougile-util'
import {
  DataObjectId,
  sharingGuestAccount,
  User,
  UserAccount,
  yougileUser,
  youGileUserId
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Sync} from '@client/sync'
import {useForceUpdate, useGuestInfo, useSyncEvent} from '@containers/hooks'
import {UserProxy} from '@extensions/proxies/user-proxy'

export const getUserProxyByUserId = (
  userId?: DataObjectId
): UserProxy | undefined => {
  if (!userId) {
    return undefined
  }

  const emptyUser = new User('' as DataObjectId, userId, '')
  let user: User
  let account: UserAccount | undefined
  if (userId === youGileUserId) {
    user = yougileUser
  } else if (userId === sharingGuestAccount.id) {
    user = emptyUser
    account = sharingGuestAccount
  } else {
    const box = Sync.getCurrentSandbox()
    user = box.getUser(userId) ?? emptyUser
    account = Accounts.get(userId, true)
  }
  return UserProxy.create(user, account)
}

export const useUserProxy = (userId?: DataObjectId): UserProxy | undefined => {
  const forceUpdate = useForceUpdate()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const update = useCallback(() => {
    if (!userId) {
      return
    }
    const account = Accounts.get(userId, true)
    if (UserAccount.isInDnd(account)) {
      const time = account.dndUntil - Time.now() + 1000
      timeoutRef.current = setTimeout(update, time)
    }
    forceUpdate()
  }, [userId, timeoutRef])

  useSyncEvent('current', {filter: userId}, update, [userId])

  useEffect(() => {
    if (!userId) {
      return Util.noop
    }
    const updateHid = Accounts.addListener({account: userId, cb: update})
    return () => Accounts.removeListener(updateHid)
  }, [userId, update])

  useEffect(
    () => () => timeoutRef.current && clearTimeout(timeoutRef.current),
    [timeoutRef]
  )

  return getUserProxyByUserId(userId)
}

export const useUserProxies = (userIds: DataObjectId[]): UserProxy[] => {
  const update = useForceUpdate()
  useSyncEvent('current', {filter: userIds}, update, [userIds])
  return userIds
    .map(getUserProxyByUserId)
    .filter((x: UserProxy | undefined): x is UserProxy => !!x)
}

export const useGuestUserProxy = (
  guestId?: DataObjectId
): UserProxy | undefined => {
  const guestInfo = useGuestInfo(guestId)
  return useMemo(() => {
    if (!guestId || !guestInfo) {
      return undefined
    }
    const user = new User('' as DataObjectId, guestId, '')
    const account = new UserAccount(guestId, guestInfo.name, guestInfo.avatar)
    return UserProxy.create(user, account)
  }, [guestId, guestInfo])
}
